import { GroupResultSelectionSupport } from '../../Group-Result/group-result-selection-support';
import { GridGroupingPagerService } from './grid-grouping-pager.service';

const defaultSelectionProperty =
  GroupResultSelectionSupport.selectionStateProperty;

/**
 * Shared helpers so any grid using the grouping pager can get correct
 * selection behavior (group checkbox applies to all pages, per-record
 * selection preserved when paging) with minimal code.
 *
 * Use in:
 * 1. Data loader – after setting page.data = result, call applyInheritedCheckedToResult.
 * 2. selectionChange – when groupIndex is set, call applyGroupHeaderSelection.
 * 3. getItems/save – use service.getAllLoadedRecordsFromPager() then merge with grid
 *    data and dedupe by your row id.
 */
export class GridGroupingPagerSelectionHelper {
  /**
   * Call when you first load a group's data (page.data = result). Applies
   * this group's or an ancestor's checked state to every record so parent
   * selection applies to all pages. Do not call on page change (getGroupData).
   */
  static applyInheritedCheckedToResult<T>(
    service: GridGroupingPagerService<T>,
    groupIndex: string,
    result: T[],
    selectionStateProperty: string = defaultSelectionProperty
  ): void {
    const page = service.getGroupPage(groupIndex);
    const inheritedChecked =
      page.checked ?? service.getAncestorChecked(groupIndex);
    if (inheritedChecked == null || !result?.length) return;
    page.checked = inheritedChecked;
    const sel = selectionStateProperty;
    result.forEach((i) => {
      const record = i as Record<string, { checked?: boolean } | undefined>;
      if (record[sel]) {
        record[sel].checked = inheritedChecked;
      } else {
        record[sel] = { checked: inheritedChecked };
      }
    });
  }

  /**
   * Call from selectionChange when the user checks/unchecks a group header
   * (when groupIndex is present). Sets page.checked and applies the checked
   * state to all records in this group and descendant groups' page.data.
   */
  static applyGroupHeaderSelection<T>(
    service: GridGroupingPagerService<T>,
    groupIndex: string,
    checked: boolean,
    selectionStateProperty: string = defaultSelectionProperty
  ): void {
    const page = service.getGroupPage(groupIndex);
    if (!page) return;
    page.checked = checked;
    const sel = selectionStateProperty;
    service.forEachRecordInGroupAndDescendants(groupIndex, (i) => {
      const record = i as Record<string, { checked?: boolean } | undefined>;
      if (record[sel]) {
        record[sel].checked = checked;
      } else {
        record[sel] = { checked };
      }
    });
  }
}
